import React, { useState, useEffect } from "react";
import { http } from "../../axios/axios";
import AdminHeader from "../../component/AdminHeader";
import StatCard from "../../component/StatCard";
import { Layers } from "lucide-react";
import Pagination from "../../component/Pagination";
import { AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

import AdminComplaintRowActions from "./AdminComplaintComponent/AdminComplaintRowActions";
import ComplaintModal from "./AdminComplaintComponent/ComplaintModal";

export default function ComplaintsAdmin() {
  const [allComplaints, setAllComplaints] = useState([]);
  const [complaintDetail, setComplaintDetail] = useState(null);

  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(40);
  const [status, setStatus] = useState("all");
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState("details");
  const [loadingComplaint, setLoadingComplaint] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  const [count, setCount] = useState({
    pending: 0,
    resolved: 0,
  });

  const fetchComplaints = async () => {
    try {
      const res = await http.get("complaints/adminComplaints", {
        params: {
          page,
          limit,
          status: status === "all" ? undefined : status,
        },
      });

      setAllComplaints(res.data?.data);
      setPagination(res.data?.pagination);
      setCount(res.data.counts);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchComplaintById = async (id) => {
    try {
      setLoadingComplaint(true);

      const res = await http.get(`complaints/${id}`);
      setComplaintDetail(res.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingComplaint(false);
    }
  };
  const handleResolve = async (id, resolutionNote) => {
    if (!id) return;

    try {
      setResolvingId(id);
      const { data } = await http.patch(`complaints/${id}/resolve`, {
        resolutionNote,
      });

      setAllComplaints((current) =>
        current.map((complaint) =>
          complaint._id === id ? data.complaint : complaint,
        ),
      );
      setComplaintDetail(data.complaint);
      setCount((current) => ({
        pending: Math.max(0, current.pending - 1),
        resolved: current.resolved + 1,
      }));
      setOpenModal(false);
      toast.success(
        data.message ||
          "Complaint resolved and resolution email sent successfully.",
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Unable to send resolution email. Complaint was not marked as resolved.",
      );
    } finally {
      setResolvingId(null);
    }
  };
  const deleteComplaint = async (id) => {
    if (!id) return;
    const confirmed = window.confirm(
      "Are you sure you want to mark this complaint as resolved?",
    );

    if (!confirmed) return;

    try {
      await http.delete(`complaints/${id}/delete`);
    } catch (error) {
      console.error(error);
    }

    await fetchComplaints();
  };

  useEffect(() => {
    fetchComplaints();
  }, [page, status]);

  const handleViewComplaint = async (id) => {
    setComplaintDetail(null);
    setModalMode("details");
    setOpenModal(true);
    await fetchComplaintById(id);
  };

  const handleOpenResolution = async (id) => {
    setComplaintDetail(null);
    setModalMode("resolve");
    setOpenModal(true);
    await fetchComplaintById(id);
  };

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader title="Customer Complaints" />

      <div className="flex flex-col md:flex-row w-full mb-8  gap-4">
        <StatCard
          title="Total Complaints"
          count={pagination.total}
          icon={Layers}
        />
        <StatCard title="Pending" count={count.pending} icon={Layers} />

        <StatCard title="Resolved" count={count.resolved} icon={Layers} />
      </div>

      <div className="mb-6 flex items-center justify-end gap-3">
        <label className="text-sm text-slate-400">Status</label>

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-white outline-none focus:border-blue-500"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      <div className="space-y-4">
        {allComplaints.length === 0 ? (
          <div
            className="
        p-8
        rounded-2xl
        bg-white/5
        border border-white/10
        text-center
        text-slate-400
      "
          >
            No complaints found
          </div>
        ) : (
          allComplaints.map((complain) => (
            <div
              key={complain._id}
              className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-4
          p-4
          rounded-lg
          bg-slate-900/40
          border
          border-slate-800
          hover:border-slate-700
          hover:bg-slate-900/60
          transition
        "
            >

              <div className="space-y-1">
                <h3 className="text-white font-semibold text-lg">
                  {complain.name}
                </h3>
                <p className="text-sm text-slate-400">{complain.phone}</p>
                <div className="flex flex-wrap gap-2 text-xs mt-2">
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">
                    Support: {complain.support}
                  </span>
                  <span
                    className={`px-2 py-1 rounded ${
                      complain.installedByUs
                        ? "bg-green-900 text-green-300"
                        : "bg-yellow-900 text-yellow-300"
                    }`}
                  >
                    {complain?.installedByUs
                      ? "Installed by Us"
                      : "Outside Installation"}
                  </span>
                  <span
                    className={`px-2 py-1 rounded capitalize text-center ${
                      complain?.status === "pending"
                        ? "bg-orange-900 text-orange-300"
                        : complain?.status === "resolved"
                          ? "bg-green-900 text-green-300"
                          : "bg-blue-900 text-blue-300"
                    }`}
                  >
                    {complain.status}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Created: {new Date(complain.createdAt).toLocaleString()}
                </p>
                {complain.resolutionEmailSent && (
                  <p className="text-xs text-green-400">
                    Resolution email sent
                    {complain.resolutionEmailSentAt
                      ? `: ${new Date(
                          complain.resolutionEmailSentAt,
                        ).toLocaleString()}`
                      : ""}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 ">
                <AdminComplaintRowActions
                  onView={() => handleViewComplaint(complain._id)}
                  onResolve={() => handleOpenResolution(complain._id)}
                  onDelete={() => deleteComplaint(complain._id)}
                  isResolved={complain.status === "resolved"}
                  isSending={resolvingId === complain._id}
                />
              </div>
            </div>
          ))
        )}
        {allComplaints.length > 0 && (
          <Pagination
            page={page}
            totalPages={pagination.totalPage}
            onPrevious={() => setPage((prev) => prev - 1)}
            onNext={() => setPage((prev) => prev + 1)}
          />
        )}
      </div>

      <AnimatePresence>
        {openModal && (
          <ComplaintModal
            loading={loadingComplaint}
            complaintDetail={complaintDetail}
            onClose={() => setOpenModal(false)}
            handleResolve={handleResolve}
            mode={modalMode}
            isResolving={resolvingId === complaintDetail?._id}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
